import { h, type FunctionalComponent } from 'vue';
import catppuccinIcons from '@iconify-json/catppuccin/icons.json';

// --- icon name resolution ---

// exact filename -> icon name
const FILE_NAMES: Record<string, string> = {
	'.gitignore': 'git',
	'.gitattributes': 'git',
	'.gitmodules': 'git',
	'.editorconfig': 'editorconfig',
	'.prettierrc': 'prettier',
	'.prettierignore': 'prettier-ignore',
	'.eslintrc': 'eslint',
	'.eslintignore': 'eslint',
	'.stylelintrc': 'stylelint',
	'.stylelintignore': 'stylelint-ignore',
	'.env': 'env',
	'.env.local': 'env',
	'.env.development': 'env',
	'.env.production': 'env',
	'.env.test': 'env',
	'.npmrc': 'npm',
	'.npmignore': 'npm-ignore',
	'.yarnrc': 'yarn',
	'.yarnrc.yml': 'yarn',
	'.browserslistrc': 'browserslist',
	dockerfile: 'docker',
	'docker-compose.yml': 'docker-compose',
	'docker-compose.yaml': 'docker-compose',
	'.dockerignore': 'docker-ignore',
	makefile: 'makefile',
	'cmakelists.txt': 'cmake',
	license: 'license',
	licence: 'license',
	'license.md': 'license',
	'licence.md': 'license',
	'license.txt': 'license',
	readme: 'readme',
	'readme.md': 'readme',
	'readme.txt': 'readme',
	contributing: 'contributing',
	'contributing.md': 'contributing',
	changelog: 'changelog',
	'changelog.md': 'changelog',
	security: 'security',
	'security.md': 'security',
	todo: 'todo',
	'todo.md': 'todo',
	'robots.txt': 'robots',
	'cargo.toml': 'cargo',
	'cargo.lock': 'cargo-lock',
	'go.mod': 'go',
	'go.sum': 'go',
	'package.json': 'package-json',
	'package-lock.json': 'npm-lock',
	'yarn.lock': 'yarn-lock',
	'pnpm-lock.yaml': 'pnpm-lock',
	'bun.lockb': 'bun-lock',
	'deno.json': 'deno',
	'deno.jsonc': 'deno',
	'deno.lock': 'deno-lock',
	'tsconfig.json': 'typescript-config',
	'tsconfig.node.json': 'typescript-config',
	'tsconfig.app.json': 'typescript-config',
	'jsconfig.json': 'javascript',
	'vite.config.ts': 'vite',
	'vite.config.js': 'vite',
	'vitest.config.ts': 'vitest',
	'vitest.config.js': 'vitest',
	'webpack.config.js': 'webpack',
	'webpack.config.ts': 'webpack',
	'rollup.config.js': 'rollup',
	'rollup.config.ts': 'rollup',
	'tailwind.config.js': 'tailwind',
	'tailwind.config.ts': 'tailwind',
	'postcss.config.js': 'postcss',
	'postcss.config.ts': 'postcss',
	'nuxt.config.ts': 'nuxt',
	'nuxt.config.js': 'nuxt',
	'next.config.js': 'next',
	'next.config.ts': 'next',
	'astro.config.mjs': 'astro-config',
	'svelte.config.js': 'svelte-config',
	'biome.json': 'biome',
	'biome.jsonc': 'biome',
	'.prettierrc.js': 'prettier',
	'.prettierrc.json': 'prettier',
	'.prettierrc.yaml': 'prettier',
	'.prettierrc.yml': 'prettier',
	'.prettierrc.toml': 'prettier',
	'.eslintrc.js': 'eslint',
	'.eslintrc.json': 'eslint',
	'.eslintrc.yaml': 'eslint',
	'.eslintrc.yml': 'eslint',
	'eslint.config.js': 'eslint',
	'eslint.config.ts': 'eslint',
	'eslint.config.mjs': 'eslint',
	'prettier.config.js': 'prettier',
	'prettier.config.ts': 'prettier',
	'playwright.config.ts': 'playwright',
	'playwright.config.js': 'playwright',
	'cypress.config.ts': 'cypress',
	'cypress.config.js': 'cypress',
	'jest.config.ts': 'jest',
	'jest.config.js': 'jest',
	'.babelrc': 'babel',
	'babel.config.js': 'babel',
	'babel.config.json': 'babel',
	'nginx.conf': 'nginx',
	'prisma.schema': 'prisma',
};

// file extension -> icon name (without leading dot)
const FILE_EXTENSIONS: Record<string, string> = {
	// typescript
	ts: 'typescript',
	mts: 'typescript',
	cts: 'typescript',
	tsx: 'typescript-react',
	'd.ts': 'typescript-def',
	// javascript
	js: 'javascript',
	mjs: 'javascript',
	cjs: 'javascript',
	jsx: 'javascript-react',
	// web
	vue: 'vue',
	svelte: 'svelte',
	astro: 'astro',
	html: 'html',
	htm: 'html',
	css: 'css',
	scss: 'sass',
	sass: 'sass',
	less: 'less',
	styl: 'css',
	// templating
	pug: 'pug',
	ejs: 'ejs',
	hbs: 'handlebars',
	njk: 'nunjucks',
	twig: 'twig',
	liquid: 'liquid',
	// data
	json: 'json',
	jsonc: 'json',
	json5: 'json',
	yaml: 'yaml',
	yml: 'yaml',
	toml: 'toml',
	xml: 'xml',
	csv: 'csv',
	ini: 'properties',
	properties: 'properties',
	env: 'env',
	// markdown / docs
	md: 'markdown',
	mdx: 'markdown',
	txt: 'text',
	pdf: 'pdf',
	tex: 'text',
	rst: 'text',
	// shell
	sh: 'bash',
	bash: 'bash',
	zsh: 'bash',
	fish: 'bash',
	ps1: 'powershell',
	bat: 'batch',
	cmd: 'batch',
	// systems
	c: 'c',
	h: 'c-header',
	cpp: 'cpp',
	cc: 'cpp',
	cxx: 'cpp',
	hpp: 'cpp-header',
	hxx: 'cpp-header',
	cs: 'csharp',
	rs: 'rust',
	go: 'go',
	zig: 'zig',
	nim: 'nim',
	odin: 'odin',
	d: 'd',
	asm: 'assembly',
	s: 'assembly',
	wasm: 'web-assembly',
	// jvm
	java: 'java',
	kt: 'kotlin',
	kts: 'kotlin',
	scala: 'scala',
	clj: 'clojure',
	cljs: 'clojure',
	groovy: 'gradle',
	gradle: 'gradle',
	// scripting
	py: 'python',
	pyc: 'python-compiled',
	pyi: 'python',
	rb: 'ruby',
	erb: 'ruby',
	php: 'php',
	lua: 'lua',
	pl: 'perl',
	pm: 'perl',
	r: 'r',
	rmd: 'rmd',
	// functional
	hs: 'haskell',
	ex: 'elixir',
	exs: 'elixir',
	erl: 'erlang',
	ml: 'ocaml',
	mli: 'ocaml',
	re: 'reason',
	res: 'rescript',
	fs: 'fsharp',
	fsx: 'fsharp',
	// mobile
	swift: 'swift',
	dart: 'dart',
	// config
	lock: 'lock',
	log: 'log',
	diff: 'diff',
	patch: 'diff',
	// devops
	tf: 'terraform',
	hcl: 'terraform',
	nix: 'nix',
	// query / schema
	sql: 'database',
	graphql: 'graphql',
	gql: 'graphql',
	prisma: 'prisma',
	// images
	png: 'image',
	jpg: 'image',
	jpeg: 'image',
	gif: 'image',
	webp: 'image',
	bmp: 'image',
	ico: 'image',
	avif: 'image',
	svg: 'svg',
	// audio
	mp3: 'audio',
	wav: 'audio',
	flac: 'audio',
	aac: 'audio',
	ogg: 'audio',
	weba: 'audio',
	m4a: 'audio',
	// video
	mp4: 'video',
	webm: 'video',
	mov: 'video',
	avi: 'video',
	mkv: 'video',
	// misc
	zip: 'zip',
	tar: 'zip',
	gz: 'zip',
	bz2: 'zip',
	xz: 'zip',
	'7z': 'zip',
	rar: 'zip',
	bin: 'binary',
	exe: 'binary',
	dll: 'binary',
	so: 'binary',
	dylib: 'binary',
	// shader
	glsl: 'shader',
	vert: 'shader',
	frag: 'shader',
	hlsl: 'shader',
	wgsl: 'shader',
};

// folder name -> icon name (without 'folder-' prefix — we prepend it)
const FOLDER_NAMES: Record<string, string> = {
	src: 'src',
	lib: 'lib',
	dist: 'dist',
	build: 'dist',
	out: 'dist',
	node_modules: 'node',
	components: 'components',
	composables: 'composables',
	hooks: 'hooks',
	utils: 'utils',
	helpers: 'utils',
	config: 'config',
	configs: 'config',
	assets: 'assets',
	public: 'public',
	static: 'public',
	images: 'images',
	img: 'images',
	fonts: 'fonts',
	styles: 'styles',
	css: 'styles',
	tests: 'tests',
	test: 'tests',
	__tests__: 'tests',
	spec: 'tests',
	specs: 'tests',
	docs: 'docs',
	doc: 'docs',
	scripts: 'scripts',
	bin: 'scripts',
	api: 'api',
	routes: 'routes',
	pages: 'routes',
	views: 'views',
	layouts: 'layouts',
	templates: 'templates',
	middleware: 'middleware',
	plugins: 'plugins',
	server: 'server',
	client: 'client',
	shared: 'shared',
	common: 'shared',
	core: 'core',
	types: 'types',
	typings: 'types',
	locales: 'locales',
	i18n: 'locales',
	db: 'database',
	database: 'database',
	migrations: 'database',
	docker: 'docker',
	'.docker': 'docker',
	'.github': 'github',
	'.gitlab': 'gitlab',
	'.vscode': 'vscode',
	'.idea': 'intellij',
	git: 'git',
	'.git': 'git',
	packages: 'packages',
	functions: 'functions',
	controllers: 'controllers',
	services: 'server',
	models: 'database',
	constants: 'constant',
	content: 'content',
	examples: 'examples',
	mocks: 'mocks',
	__mocks__: 'mocks',
	fixtures: 'mocks',
	storybook: 'storybook',
	'.storybook': 'storybook',
	coverage: 'coverage',
	temp: 'temp',
	tmp: 'temp',
	'.cache': 'temp',
	private: 'private',
	security: 'security',
	audio: 'audio',
	video: 'video',
	svg: 'svg',
};

// type-safe access to icon bodies
const icons = catppuccinIcons.icons as Record<string, { body: string } | undefined>;

// --- macchiato hex -> CSS custom property mapping ---
// the shipped icon bodies use macchiato (dark) palette hex values;
// we replace them with CSS vars so the CSS cascade handles light/dark switching

const MACCHIATO_TO_VAR: Record<string, string> = {
	'#7dc4e4': 'var(--color-ctp-sapphire)',
	'#8087a2': 'var(--color-ctp-overlay1)',
	'#8aadf4': 'var(--color-ctp-blue)',
	'#8bd5ca': 'var(--color-ctp-teal)',
	'#91d7e3': 'var(--color-ctp-sky)',
	'#a6da95': 'var(--color-ctp-green)',
	'#b7bdf8': 'var(--color-ctp-lavender)',
	'#c6a0f6': 'var(--color-ctp-mauve)',
	'#cad3f5': 'var(--color-ctp-text)',
	'#ed8796': 'var(--color-ctp-red)',
	'#ee99a0': 'var(--color-ctp-maroon)',
	'#eed49f': 'var(--color-ctp-yellow)',
	'#f0c6c6': 'var(--color-ctp-flamingo)',
	'#f4dbd6': 'var(--color-ctp-rosewater)',
	'#f5a97f': 'var(--color-ctp-peach)',
	'#f5bde6': 'var(--color-ctp-pink)',
};

const hexPattern = new RegExp(Object.keys(MACCHIATO_TO_VAR).join('|'), 'gi');
const bodyCache = new Map<string, string>();

const transformBody = (body: string): string => {
	return body.replace(hexPattern, (match) => MACCHIATO_TO_VAR[match.toLowerCase()] ?? match);
};

/**
 * Get a transformed (CSS-var-ified) icon body, with caching.
 */
const getIconBody = (iconName: string): string | undefined => {
	const cached = bodyCache.get(iconName);
	if (cached) return cached;
	const raw = icons[iconName]?.body;
	if (!raw) return undefined;
	const transformed = transformBody(raw);
	bodyCache.set(iconName, transformed);
	return transformed;
};

/**
 * Get the catppuccin icon SVG body for a given filename.
 * Falls back to the generic file icon if no specific match exists.
 */
export const getFileIconBody = (filename: string): string | undefined => {
	const name = resolveFileIconName(filename);
	if (name) return getIconBody(name);
	// fall back to generic file icon
	return getIconBody('file');
};

/**
 * Get the catppuccin icon SVG body for a folder name.
 * @param folderName - just the folder's basename (e.g. "src", ".github")
 * @param open - whether the folder is expanded
 */
export const getFolderIconBody = (folderName: string, open: boolean): string | undefined => {
	const lower = folderName.toLowerCase();
	const mapped = FOLDER_NAMES[lower];
	if (mapped) {
		const iconName = open ? `folder-${mapped}-open` : `folder-${mapped}`;
		const body = getIconBody(iconName);
		if (body) return body;
	}
	// fall back to generic folder icon
	return getIconBody(open ? 'folder-open' : 'folder');
};

/**
 * Get the catppuccin symlink icon SVG body.
 * @param folder - true for folder symlink, false for file symlink
 * @param open - whether the folder is expanded (only relevant when folder=true)
 */
export const getSymlinkIconBody = (folder: boolean, open: boolean): string | undefined => {
	if (folder) return getIconBody(open ? 'folder-symlink-open' : 'folder-symlink');
	return getIconBody('symlink');
};

/**
 * Resolve a filename to its catppuccin icon name.
 */
export const resolveFileIconName = (filename: string): string | undefined => {
	const lower = filename.toLowerCase();

	// exact filename match first
	const exact = FILE_NAMES[lower];
	if (exact && icons[exact]) return exact;

	// compound extension (e.g. "d.ts", "spec.ts")
	const dotParts = lower.split('.');
	if (dotParts.length > 2) {
		const compoundExt = dotParts.slice(-2).join('.');
		const compound = FILE_EXTENSIONS[compoundExt];
		if (compound && icons[compound]) return compound;
	}

	// simple extension
	const ext = dotParts.pop();
	if (ext) {
		const simple = FILE_EXTENSIONS[ext];
		if (simple && icons[simple]) return simple;
	}

	return undefined;
};

/**
 * Create a Vue functional component that renders a specific catppuccin file icon.
 * Used for panel tab icons where `panel.icon` expects a Component.
 * Falls back to undefined if no icon found (caller should use a default).
 */
export const createFileIconComponent = (filename: string): FunctionalComponent | undefined => {
	const body = getFileIconBody(filename);
	if (!body) return undefined;

	const component: FunctionalComponent = (_props, { attrs }) => {
		return h('svg', {
			xmlns: 'http://www.w3.org/2000/svg',
			viewBox: '0 0 16 16',
			innerHTML: body,
			...attrs,
			class: attrs.class,
		});
	};
	component.displayName = `FileIcon(${filename})`;
	return component;
};
